import {
  Divider,
  Flex,
  Spacer,
  useMediaQuery,
  useTheme,
} from '@chakra-ui/react';
import AmenitiesList from '@molecules/AmenitiesList';
import BEImageList from '@molecules/BEImageList';
import ModalComponent, { ModalProps } from '@molecules/BEOffScreen/Modal';
import BEPills from '@molecules/BEPills';
import Text from '@molecules/Text';
import AccommodationGallery from './AccommodationGallery';
import Accommodation from '@data/models/Accommodation';
import { useTranslation } from 'react-i18next';

type Props = {
  accommodation: Accommodation;
  handleClickImage?: (index: number) => void;
} & ModalProps;

const useResponsive = () => {
  const theme = useTheme();
  const breakpoints = theme.breakpoints;

  const [isBetweenMDAndLG] = useMediaQuery(
    `(min-width: ${breakpoints.md}) and (max-width: ${breakpoints.lg})`
  );

  const [isBetweenLGAndXL] = useMediaQuery(
    `(min-width: ${breakpoints.lg}) and (max-width: ${breakpoints.xl})`
  );

  const [isBetweenXLAnd2XL] = useMediaQuery(
    `(min-width: ${breakpoints.xl}) and (max-width: 1500px)`
  );

  if (isBetweenMDAndLG) {
    return 640;
  }

  if (isBetweenLGAndXL) {
    return 768;
  }

  if (isBetweenXLAnd2XL) {
    return 1000;
  }

  return 1280;
};

const AccommodationDetails = ({
  accommodation,
  handleClickImage,
  ...modalProps
}: Props) => {
  //const [activeIndex, setActiveIndex] = useState(index);
  const width = useResponsive();
  const { t } = useTranslation();

  const { name, images, adults, kids, description, amenities } = accommodation;

  function onClickImage(index: number) {
    handleClickImage?.(index);
  }

  function renderGallery() {
    return (
      <>
        {width > 640 ? (
          <AccommodationGallery images={images} onClickItem={onClickImage} />
        ) : (
          <BEImageList
            images={images.map((image) => image.thumb)}
            responsive={{
              xs: {
                itemHeight: 280,
                multiplier: 0.55,
              },
              sm: {
                itemHeight: 280,
                multiplier: 0.55,
              },
              md: {
                itemHeight: 280,
                multiplier: 0.55,
              },
              lg: {
                itemHeight: 280,
                multiplier: 0.55,
              },
              xl: {
                itemHeight: 280,
                multiplier: 0.55,
              },
              '2xl': {
                itemHeight: 280,
                multiplier: 0.55,
              },
            }}
            onClickItem={onClickImage}
          />
        )}
      </>
    );
  }

  return (
    <ModalComponent
      {...modalProps}
      hideFooter={true}
      hideHeader={true}
      width={width}
    >
      <Flex flexDir="column" gap="16px" mt="72px" mb="48px" px="16px">
        {renderGallery()}
        <Spacer />
        <Text type="h1">{name}</Text>
        <Flex gap="8px">
          <Text>Accommodates</Text>
          {adults > 0 && <BEPills iconRight="user">{adults}</BEPills>}
          {kids > 0 && <BEPills iconRight="child-reaching">{kids}</BEPills>}
        </Flex>
        <Text html={description} />
        <Divider my="16px" />
        {amenities && amenities.length > 0 && (
          <>
            <Text type="h3">{t('accommodation-amenities')}</Text>
            <AmenitiesList amenities={amenities} />
          </>
        )}
      </Flex>
    </ModalComponent>
  );
};

export default AccommodationDetails;
